import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { enrollStudent, listAllCourses, listStudents, revokeEnrollment, type CourseRow, type StudentRow } from '../../lib/adminApi';
import { formatDateTime, timeAgo } from '../../lib/format';
import { friendlyError } from '../../lib/supabase';
import { Badge, Button, Callout, ProgressBar, Spinner, TextField } from '../../components/ui';

export function AdminStudents() {
  const { isAdmin } = useAuth();
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState<StudentRow[] | null>(null);
  const [courses, setCourses] = useState<CourseRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = (s = search) => listStudents(s).then(setRows).catch((e) => setError(friendlyError(e)));
  useEffect(() => { listAllCourses().then(setCourses).catch(() => {}); }, []);
  useEffect(() => { const t = setTimeout(() => { void load(search); }, 250); return () => clearTimeout(t); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [search]);

  const title = (id: string) => courses.find((c) => c.id === id)?.title ?? 'Course';

  async function act(fn: () => Promise<void>, ok: string) {
    setError(null); setNotice(null);
    try { await fn(); setNotice(ok); await load(); } catch (e) { setError(friendlyError(e)); }
  }

  return (
    <>
      <h1 style={{ margin: 0, fontSize: 28, lineHeight: '36px', fontWeight: 400, color: 'var(--brand)' }}>Students</h1>
      <TextField label="Search by name or email" type="search" value={search} onChange={(e) => setSearch(e.target.value)} />
      {error && <Callout variant="error">{error}</Callout>}
      {notice && <Callout variant="note" role="status">{notice}</Callout>}
      {!rows ? <Spinner /> : (
        <div className="table-wrap">
          <table className="table" style={{ minWidth: 900 }}>
            <thead>
              <tr><th>Student</th><th>Last sign-in</th><th>Courses and progress</th>{isAdmin && <th>Complimentary enrollment</th>}</tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.id}>
                  <td>
                    <Link to={`/admin/students/${s.id}`} style={{ fontWeight: 600 }}>{s.full_name || s.email || 'Unnamed'}</Link>
                    <div className="small muted">{s.email}</div>
                    {s.organization && <div className="small muted">{s.organization}</div>}
                  </td>
                  <td title={formatDateTime(s.last_sign_in_at)} style={{ whiteSpace: 'nowrap' }}>{timeAgo(s.last_sign_in_at)}</td>
                  <td>
                    <div className="stack" style={{ gap: 10, minWidth: 280 }}>
                      {s.enrollments.length === 0 && <span className="muted">Not enrolled</span>}
                      {s.enrollments.map((e) => (
                        <div key={e.id} className="stack" style={{ gap: 4 }}>
                          <div className="row" style={{ gap: 8 }}>
                            <span style={{ fontWeight: 600, flex: '1 1 160px' }}>{title(e.course_id)}</span>
                            {e.status === 'completed' ? <Badge tone="success">Completed</Badge>
                              : e.status === 'revoked' ? <Badge tone="danger">Removed</Badge>
                              : e.completed_lessons === 0 ? <Badge>Not started</Badge> : <Badge tone="brand">In progress</Badge>}
                          </div>
                          {e.status !== 'revoked' && (
                            <ProgressBar size="sm" value={e.percent_complete} label={`${e.completed_lessons} of ${e.required_lessons} lessons · last activity ${timeAgo(e.last_access_at).toLowerCase()}`} />
                          )}
                          {isAdmin && e.status !== 'revoked' && (
                            <div><Button size="sm" variant="ghost" onClick={() => { if (confirm('Remove this student’s access to the course? Their progress is kept.')) void act(() => revokeEnrollment(e.id), 'Access removed.'); }}>Remove access</Button></div>
                          )}
                        </div>
                      ))}
                    </div>
                  </td>
                  {isAdmin && (
                    <td>
                      <EnrollPicker courses={courses.filter((c) => !s.enrollments.some((e) => e.course_id === c.id && e.status !== 'revoked'))}
                        onEnroll={(cid) => act(() => enrollStudent(s.id, cid), `Enrolled ${s.full_name || s.email} in ${title(cid)}.`)} />
                    </td>
                  )}
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={4} className="muted">No matching accounts.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function EnrollPicker({ courses, onEnroll }: { courses: CourseRow[]; onEnroll: (courseId: string) => void }) {
  const [cid, setCid] = useState('');
  if (!courses.length) return <span className="small muted">Enrolled in all courses</span>;
  return (
    <div className="row" style={{ gap: 6 }}>
      <select className="bl-select" aria-label="Course" value={cid} onChange={(e) => setCid(e.target.value)} style={{ maxWidth: 220 }}>
        <option value="">Choose a course…</option>
        {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
      </select>
      <Button size="sm" disabled={!cid} onClick={() => { onEnroll(cid); setCid(''); }}>Enroll</Button>
    </div>
  );
}
